import type { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import shallow from 'zustand/shallow';
import type { SelectOption } from '../../components';
import { Select, SmartSearch, TabPills } from '../../components';
import { useCcConfig } from '../../store';
import { EventType } from './graphql';
import { useEventsStore } from './store';
import { ValidatorFilter } from './validator-filter';

/*
 * Only Live and My Events are surfaced as tabs. Upcoming / Past / Favorites are
 * hidden from the bar but still reachable by URL (/events/<slug>) and via the
 * user dropdown links; My Events now also folds in the user's favorites.
 */
const EVENT_TABS = [
  { key: 'live', label: 'Live' },
  { key: 'mine', label: 'My Events' }
];

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  /* Keep every control at its natural size; wrap only on a real overflow. */
  > * {
    flex-shrink: 0;
  }
`;

// Fixed-width wrapper so the search doesn't greedily claim the row.
const SearchBox = styled.div`
  width: 250px;
`;

// Compact control that will eventually scale the event cards. Visual only.
const SizeSlider = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 0 12px;
  height: 38px;
  border-radius: 10px;
  background: rgba(17, 27, 51, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.08);

  > i {
    color: var(--white60);
    font-size: 13px;
  }

  input[type='range'] {
    -webkit-appearance: none;
    appearance: none;
    width: 120px;
    height: 4px;
    border-radius: 999px;
    outline: none;
    cursor: pointer;
  }

  input[type='range']::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: rgb(78, 95, 253);
    border: none;
  }

  input[type='range']::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: rgb(78, 95, 253);
  }
`;

const EVENT_TYPE_OPTIONS: SelectOption<EventType | undefined>[] = [
  { value: undefined, label: 'All Events' },
  { value: EventType.Produced, label: 'Produced' },
  { value: EventType.Static, label: 'Static' },
  { value: EventType.Mobile, label: 'Mobile' },
  { value: EventType.MobileKit, label: 'Mobile Kit' },
  { value: EventType.SimLive, label: 'Simulated Live' },
  { value: EventType.Test, label: 'Test' }
];

const EventTypeFilter = (): ReactElement => {
  const { evType, setEvType } = useEventsStore((v) => ({
    evType: v.filters.eventType,
    setEvType: v.setEventType
  }), shallow);

  return <Select options={EVENT_TYPE_OPTIONS} value={evType} onChange={setEvType} />;
};

// Four discrete card sizes (levels 1–4).
const SIZE_MIN = 1;
const SIZE_MAX = 4;

const CardSizeSlider = (): ReactElement | null => {
  const { size, setSize, viewMode } = useCcConfig((v) => ({
    size: v.cardSize,
    setSize: v.setCardSize,
    viewMode: v.viewMode
  }), shallow);

  // Card size only affects the board; hide it in table view.
  if (viewMode === 'table') {
    return null;
  }

  const fill = ((size - SIZE_MIN) / (SIZE_MAX - SIZE_MIN)) * 100;

  return (
    <SizeSlider title='Card size'>
      <i className='far fa-th-large' />
      <input
        type='range'
        min={SIZE_MIN}
        max={SIZE_MAX}
        step={1}
        value={size}
        onChange={(e): void => {
          setSize(Number(e.target.value));
        }}
        style={{
          background: `linear-gradient(to right, rgb(78, 95, 253) 0%, rgb(78, 95, 253) ${fill}%, rgba(255, 255, 255, 0.12) ${fill}%, rgba(255, 255, 255, 0.12) 100%)`
        }}
      />
    </SizeSlider>
  );
};

export const EventsToolbar = ({
  activeSlug,
  className
}: {
  activeSlug: string;
  className?: string;
}): ReactElement => {
  const history = useHistory();

  const { search, setSearch } = useEventsStore((v) => ({
    search: v.filters.search,
    setSearch: v.setSearch
  }), shallow);

  return (
    <Bar className={className}>
      <TabPills
        pills={EVENT_TABS.map((t) => ({
          key: t.key,
          label: t.label,
          active: t.key === activeSlug,
          onClick: (): void => {
            history.push(`/events/${t.key}`);
          }
          }
        ))}
        darker
      />

      <FilterGroup>
        {activeSlug === 'mine' && <ValidatorFilter />}

        <SearchBox>
          <SmartSearch search={search} onSearch={setSearch} />
        </SearchBox>

        <EventTypeFilter />

        <CardSizeSlider />

      </FilterGroup>
    </Bar>
  );
};
